/** @jsxImportSource @emotion/react */
import { Interpolation, Theme } from '@emotion/react';
import { Button as MuiButton } from '@mui/material';
import type {
  OverridableComponent,
  OverridableTypeMap,
} from '@mui/material/OverridableComponent';
import { ForwardedRef, MouseEventHandler, forwardRef } from 'react';
import { createStyles, Typography } from '../../';
import { IconName } from '../../icons';
import { Icon } from '../Icon';

export type Variant = 'primary' | 'secondary' | 'text' | 'icon' | 'tertiary';
export type CoreProps = React.ComponentPropsWithoutRef<'button'> & {
  text?: string;
  variant?: Variant;
  icon?: IconName;
  endIcon?: IconName;
  loading?: boolean;
  disabled?: boolean;
  utility?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  css?: Interpolation<Theme>;
  ref?: ForwardedRef<HTMLButtonElement>;
  testId?: string;
  ariaLabel?: string;
};

type VariantMap = Record<Variant, 'text' | 'contained' | 'outlined'>;

const muiVariantMap: VariantMap = {
  primary: 'contained',
  secondary: 'outlined',
  tertiary: 'outlined',
  text: 'text',
  icon: 'outlined',
};

export const CoreButton = forwardRef<HTMLButtonElement, CoreProps>(
  function CoreButton(
    {
      text,
      variant = 'primary',
      icon,
      endIcon,
      loading,
      disabled,
      utility,
      onClick,
      className,
      testId,
      css,
      ariaLabel,
    }: CoreProps,
    ref?: ForwardedRef<HTMLButtonElement>
  ): React.ReactElement {
    const styles = useStyles();
    const computedCss = [
      styles.default,
      styles[variant],
      utility && styles.utility,
      loading && styles.loading,
      { ...(variant === 'primary' ? styles.invert : {}) },
      { ...(variant === 'icon' ? styles.secondary : {}) },
      css,
    ];
    return (
      <MuiButton
        className={className}
        disableElevation
        disableRipple
        disableTouchRipple
        disabled={disabled}
        onClick={onClick}
        variant={muiVariantMap[variant]}
        ref={ref}
        data-testid={testId}
        aria-busy={loading}
        aria-label={variant === 'icon' && !text ? ariaLabel : undefined}
        css={computedCss}
        startIcon={
          loading ? (
            <Icon name="spinner" />
          ) : icon ? (
            <Icon name={icon} />
          ) : undefined
        }
        endIcon={endIcon ? <Icon name={endIcon} /> : undefined}
      >
        {!loading && <Typography variant="bodyMedium">{text}</Typography>}
      </MuiButton>
    );
  }
) as OverridableComponent<OverridableTypeMap>;

const useStyles = createStyles(({ palette, spacing }) => ({
  default: {
    height: '48px',
    padding: `${spacing.xxxs} ${spacing.xs}`,
    textTransform: 'none',
    width: '100%',
    ':focus': {
      outline: `1px solid ${palette.primary.default}`,
      outlineOffset: '1px',
    },
    ':disabled': {
      opacity: '50%',
    },
  },
  utility: {
    height: '34px',
    width: 'unset',
    padding: `${spacing.xxxs} ${spacing.xxs}`,
  },
  primary: {
    '&.MuiButton-contained': {
      background: palette.grey.x900,
      color: palette.grey.x100,
    },
  },
  secondary: {
    '&.MuiButton-outlined': {
      background: palette.grey.x200,
      color: palette.black,
      border: 'none',
    },
  },
  tertiary: {
    '&.MuiButton-outlined': {
      background: palette.white,
      color: palette.black,
      border: 'none',
    },
  },
  text: {},
  loading: {
    '&.MuiButton-outlined': {
      justifyContent: 'center',
    },
    '.MuiButton-startIcon': {
      margin: '0',
    },
  },
  icon: {
    '&.MuiButton-outlined': {
      height: spacing.s,
      width: spacing.s,
      minWidth: 'unset',
      borderRadius: '50%',
      padding: '0',
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'right',
      outline: 'none',
    },
    span: {
      height: spacing.xxs,
      width: spacing.xxs,
    },
  },
  invert: {
    svg: {
      color: palette.grey.x50,
    },
  },
  iconButtonDefault: {
    '&.MuiIconButton-root': {
      background: palette.grey.x200,
    },
  },
}));
